import React, { useRef, useEffect, useState, useContext } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { Text, Button } from "react_reusable_components";

import * as Video from "twilio-video";
import { Client as ConversationsClient } from "@twilio/conversations";

import { RemoteAuditContext } from "../../globalContext/remoteAudit/remoteAuditProvider";
import {
  setVideoStatus,
  setAudioStatus,
  setRoomStatus,
  setMultipleCamera,
  setSelectedCamera,
} from "../../globalContext/remoteAudit/remoteAuditActions";
import AuditService from "../../services/audit-service";
import { StorageService } from "../../services";
import { ThreeRow, TwoColumn, TwoRow } from "../../core/layouts";
import variables from "../../themes/base/styles/variables";
import useResponsive from "../../core/hooks/useResponsive";
import {
  getLocalizedMessage,
  isMultipleCameraDevice,
} from "../../UtilityFunctions/utils";
import { userRole } from "../../constants";
import { AuthContext } from "../../globalContext/auth/authProvider";
import ConfirmModalContainer from "../../containers/ConfirmModalContainer/ConfirmModalContainer";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";

const CallLobbyComponentStyles = {
  title: {
    fontSize: variables.fontSizeXxxlarge,
    fontWeight: variables.fontWeightBold,
    lineHeight: variables.lineHeightXxlarge,
  },
  setUpVideoTextStyle: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightSmall,
    color: variables.grey2,
  },
  style: {
    padding: variables.paddingMedium,
    paddingTop: variables.paddingSmallLG,
    width: variables.fullWidth,
    alignItems: variables.alignCenter,
  },
  topSectionStyle: { width: variables.fullWidth },
  eAuditLabelStyles: {
    fontSize: variables.fontSizeXlarge,
    lineHeight: variables.lineHeightXxlarge,
    fontWeight: variables.fontWeightBold,
  },
  setUpAudioAndVideoStyles: {
    justifyContent: variables.justifyCenter,
    margin: variables.marginCallLobbyComponent,
    gap: variables.gapXS3,
  },
  centerContent: { justifyContent: variables.justifyCenter },
  threeRowStyles: { width: variables.fullWidth },
  threeRowTopSectionStyles: { width: variables.fullWidth },
  labelsStyles: {
    gap: variables.gapSmaller,
    alignItems: variables.alignCenter,
    margin: variables.marginCallLobbyComponent,
    width: variables.fullWidth,
  },
  middleSectionStyle: {
    width: variables.fullWidth,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
  },
  cameraContainerStyles: (width) => ({
    height: variables.callLobbyComponentHeight,
    width: width,
    background: variables.textDefault,
    border: variables.border2,
    position: variables.positionRelative,
  }),
  camStartingMessage: {
    position: "absolute",
    zIndex: 2,
    color: variables.bodyBg,
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
    fontSize: variables.fontSizeXlarge,
  },
  cancelButton: {
    fontSize: variables.fontSizeNormal,
    backgroundColor: variables.bodyBg,
    color: variables.textDefault,
    border: variables.popoverBorder,
    height: variables.buttonHeightNormal,
  },
  joinButton: {
    fontSize: variables.fontSizeNormal,
    color: variables.textPrimary,
    backgroundColor: variables.primaryBg,
    height: variables.buttonHeightNormal,
    fontWeight: variables.fontWeightNormal,
  },
  customButtons: (backgroundColor) => ({
    backgroundColor: backgroundColor,
    border: variables.borderNone,
    height: variables.buttonHeightNormal,
    width: variables.buttonHeightNormal,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  bottomSectionStyle: {
    justifyContent: variables.justifyCenter,
    alignItems: variables.alignCenter,
    width: variables.widthOfDefaultScreen,
  },
  cameraAndMicStyles: {
    display: variables.dFlex,
    gap: variables.gapSmaller,
    position: variables.positionAbsolute,
    bottom: variables.bottom,
    left: variables.left,
    zIndex: variables.zIndex2,
  },
  cancelAndJoinBottomSection: {
    width: variables.fullWidth,
    marginTop: variables.marginLarge3,
  },
  twoColumnCancelAndJoinButton: (justifyContent) => ({
    gap: variables.gapSmaller,
    justifyContent: justifyContent,
    width: variables.fullWidth,
  }),
  iconStyle: {
    width: variables.iconWidthLarge,
    height: variables.iconHeightLarge,
  },
};

export default function CallLobbyComponent() {
  const intl = useIntl();
  const responsive = useResponsive();

  const { auditId } = useParams();
  const { token } = useParams();

  const authState = useContext(AuthContext)[0];
  const [videoTrack, setVideoTrack] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [joinRequestSent, setJoinRequestSent] = useState(false);

  const [remoteAuditState, remoteAuditDispatch] =
    useContext(RemoteAuditContext);
  const [, auditDispatch] = useContext(AuditContext);

  const videoRef = useRef(null);
  const localVideoTrackRef = useRef(false);

  const initializeLocalVideo = () => {
    if (!localVideoTrackRef.current) {
      localVideoTrackRef.current = true;
      setupLocalVideo();
    }
  };

  useEffect(initializeLocalVideo, []);

  useEffect(() => {
    if (!remoteAuditState.isCameraPermissionChecked)
      isMultipleCameraDevice(
        remoteAuditDispatch,
        setMultipleCamera,
        remoteAuditState.selectedCamera
      );
  }, []);

  const handleVideoTrack = (track) => {
    if (track) {
      setVideoTrack(track);
      const videoElement = track.attach();
      videoElement.style.width = variables.fullWidth;
      videoElement.style.height = variables.fullHeight;
      videoElement.style.objectFit = variables.objectFitCover;
      videoRef.current.appendChild(videoElement);
    }
  };

  const setupLocalVideo = async (selectedCam) => {
    try {
      let track;
      if (remoteAuditState?.isMultipleCamera) {
        track = await Video.createLocalVideoTrack({
          name: "camera",
          facingMode: selectedCam || remoteAuditState?.selectedCamera,
        });
      } else {
        track = await Video.createLocalVideoTrack();
      }
      handleVideoTrack(track);
    } catch (error) {
      console.error("Error setting up local video:", error);
    }
  };

  const handleToggleMute = () => {
    remoteAuditDispatch(setAudioStatus(!remoteAuditState?.audio));
  };

  const stopAndRemoveVideoTrack = () => {
    if (videoTrack) {
      videoTrack.stop();
      videoTrack.detach().forEach((element) => element.remove());
      setVideoTrack(null);
    }
  };

  const selectCamera = () => {
    const selectedCam =
      remoteAuditState?.selectedCamera === "user" ? "environment" : "user";
    remoteAuditDispatch(setSelectedCamera(selectedCam));
    return selectedCam;
  };

  const turnOffCamera = () => {
    if (videoTrack) {
      stopAndRemoveVideoTrack();
      remoteAuditDispatch(setVideoStatus(!remoteAuditState?.video));
    }
  };

  const turnOnCamera = () => {
    setupLocalVideo();
    remoteAuditDispatch(setVideoStatus(!remoteAuditState?.video));
  };

  const handleToggleCamera = () => {
    if (remoteAuditState?.video) {
      turnOffCamera();
    } else {
      turnOnCamera();
    }
  };

  const handleToggleCameraDevice = () => {
    const selectedCam = selectCamera();
    stopAndRemoveVideoTrack();
    setupLocalVideo(selectedCam);
  };

  const handleJoinCallTM = async () => {
    setJoinRequestSent(true);
    try {
      const res = await AuditService.joinRemoteCallTM({ id: auditId, intl });
      const data = res?.data?.data;
      if (data) {
        stopAndRemoveVideoTrack();
        data["userType"] = userRole.TM;
        remoteAuditDispatch(setRoomStatus(data));
      } else {
        setJoinRequestSent(false);
      }
    } catch (err) {
      setJoinRequestSent(false);
    }
  };

  const handleJoinCall = async () => {
    setJoinRequestSent(true);
    try {
      const res = await AuditService.joinRemoteCall({
        intl,
        auditId,
        isDnsd: token,
        auditDispatch,
      });
      const data = res?.data?.data;
      if (data) {
        conversationSetup(data);
      } else {
        setJoinRequestSent(false);
      }
    } catch (err) {
      setJoinRequestSent(false);
    }
  };

  const joinCall = () => {
    //because now auditId will also be there in dnsd
    if (authState?.currentRole?.id === userRole?.TM) {
      handleJoinCallTM();
    } else {
      if (token) {
        StorageService.set("public-token", token);
        if (authState?.currentRole?.id) {
          setShowModal(true);
          return;
        }
      }
      handleJoinCall();
    }
  };

  const handleConnectionStateChange = async (state, data, client) => {
    if (state === "connected") {
      const conversation = await client.getConversationBySid(
        data.conversationSid
      );
      if (conversation) {
        handleConversation(conversation, data);
      } else {
        console.log("Failed to join conversation");
      }
    }
  };

  const handleConversation = async (conversation, data) => {
    const messages = await conversation.getMessages();
    if (messages?.items?.length) {
      const msg = messages.items[messages.items.length - 1];
      if (msg?.state) {
        joinAccept(msg, data);
      }
      messages.items.forEach(async (message) => {
        await message.remove();
      });
    }
    conversation.on("messageAdded", (message) => {
      joinAccept(message, data);
      message.remove();
    });
  };

  const conversationSetup = async (data) => {
    if (data?.conversationToken) {
      const client = await new ConversationsClient(data.conversationToken);
      client.on("connectionStateChanged", (state) =>
        handleConnectionStateChange(state, data, client)
      );
    }
  };

  const joinAccept = (message, user) => {
    const data = JSON.parse(message.state.body);
    if (data?.isAccepted) {
      stopAndRemoveVideoTrack();
      data["userType"] = userRole.DNSD;
      data["userData"] = user.userData;
      remoteAuditDispatch(setRoomStatus(data));
    }
    setJoinRequestSent(false);
  };

  const onCancel = () => {
    setShowModal(false);
  };

  function getButtonText() {
    if (joinRequestSent) {
      return getLocalizedMessage(intl, "pleaseWait.label");
    } else if (token) {
      return getLocalizedMessage(intl, "asktoJoin.label");
    } else {
      return getLocalizedMessage(intl, "joinButton.label");
    }
  }

  if (showModal)
    return (
      <ConfirmModalContainer
        open={showModal}
        setOpen={setShowModal}
        isCancelBtn={false}
        confirmText={"Close"}
        onClose={onCancel}
        onConfirm={onCancel}
        question={getLocalizedMessage(intl, "dnsd.popup.noaccess_msg")}
        closeInternally={false}
      />
    );

  return (
    <TwoRow
      style={CallLobbyComponentStyles.style}
      topSectionStyle={CallLobbyComponentStyles.topSectionStyle}
      topSection={
        <Text
          style={CallLobbyComponentStyles.eAuditLabelStyles}
          type="secondary"
        >
          {getLocalizedMessage(intl, "e-audit.label")}
        </Text>
      }
      bottomSectionStyle={{
        ...CallLobbyComponentStyles.bottomSectionStyle,
        ...(!responsive.isSm && { width: variables.fullWidth }),
      }}
      bottomSection={
        <ThreeRow
          style={CallLobbyComponentStyles.threeRowStyles}
          topSectionStyle={CallLobbyComponentStyles.threeRowTopSectionStyles}
          topSection={
            <TwoRow
              style={CallLobbyComponentStyles.setUpAudioAndVideoStyles}
              topSectionStyle={CallLobbyComponentStyles.centerContent}
              topSection={
                <Text style={CallLobbyComponentStyles.title} type="secondary">
                  {getLocalizedMessage(intl, "getStarted.label")}
                </Text>
              }
              bottomSectionStyle={CallLobbyComponentStyles.centerContent}
              bottomSection={
                <Text style={CallLobbyComponentStyles.setUpVideoTextStyle}>
                  {getLocalizedMessage(intl, "setupAudioAndVideo.label")}
                </Text>
              }
            />
          }
          middleSectionStyle={CallLobbyComponentStyles.centerContent}
          middleSection={
            <CameraContainer responsive={responsive} videoRef={videoRef}>
              <CameraStartingMessage
                videoTrack={videoTrack}
                remoteAuditState={remoteAuditState}
                intl={intl}
              />
              <div style={CallLobbyComponentStyles.cameraAndMicStyles}>
                <MicButton
                  remoteAuditState={remoteAuditState}
                  handleToggleMute={handleToggleMute}
                />
                <CameraButton
                  videoTrack={videoTrack}
                  remoteAuditState={remoteAuditState}
                  handleToggleCamera={handleToggleCamera}
                />
                <ToggleCameraDeviceButton
                  videoTrack={videoTrack}
                  remoteAuditState={remoteAuditState}
                  handleToggleCameraDevice={handleToggleCameraDevice}
                />
              </div>
            </CameraContainer>
          }
          bottomSectionStyle={
            CallLobbyComponentStyles.cancelAndJoinBottomSection
          }
          bottomSection={
            <TwoColumn
              style={CallLobbyComponentStyles.twoColumnCancelAndJoinButton(
                !responsive?.isSm
                  ? variables.justifyCenter
                  : variables.justifyContentFlexEnd
              )}
              leftSection={
                <Button
                  style={CallLobbyComponentStyles.cancelButton}
                  ghost
                  icon={null}
                  onClick={() => window.close()}
                >
                  {getLocalizedMessage(intl, "cancelButton.label")}
                </Button>
              }
              rightSection={
                <Button
                  disabled={joinRequestSent}
                  style={CallLobbyComponentStyles.joinButton}
                  ghost
                  icon={null}
                  onClick={joinCall}
                >
                  {getButtonText()}
                </Button>
              }
            />
          }
        />
      }
    />
  );
}

const CameraContainer = ({ responsive, videoRef, children }) => (
  <div
    ref={videoRef}
    style={CallLobbyComponentStyles.cameraContainerStyles(
      !responsive?.isSm
        ? variables.widthOfMobileScreen
        : variables.widthOfDefaultScreen
    )}
  >
    {children}
  </div>
);

// Helper Component for Camera Starting Message
const CameraStartingMessage = ({ videoTrack, remoteAuditState, intl }) =>
  (!videoTrack || !remoteAuditState?.video) && (
    <div style={CallLobbyComponentStyles.camStartingMessage}>
      {getLocalizedMessage(intl, "manageAudit.remote.camLabel", {
        text: getLocalizedMessage(
          intl,
          !remoteAuditState?.video
            ? "manageAudit.remote.camIsOff"
            : !videoTrack && "manageAudit.remote.camIsStarting"
        ),
      })}
    </div>
  );

// Helper Component for Mic Button
const MicButton = ({ remoteAuditState, handleToggleMute }) => (
  <Button
    onClick={handleToggleMute}
    style={CallLobbyComponentStyles.customButtons(
      remoteAuditState?.audio ? variables.bodyBg : variables.primaryBg
    )}
    icon={
      <img
        style={CallLobbyComponentStyles.iconStyle}
        alt="mic-icon"
        src={
          remoteAuditState?.audio ? "/icons/mic_on.svg" : "/icons/mic_off.svg"
        }
      />
    }
  />
);

// Helper Component for Camera Button
const CameraButton = ({ videoTrack, remoteAuditState, handleToggleCamera }) => (
  <Button
    disabled={!videoTrack && remoteAuditState?.video}
    style={CallLobbyComponentStyles.customButtons(
      remoteAuditState?.video ? variables.bodyBg : variables.primaryBg
    )}
    onClick={handleToggleCamera}
    icon={
      <img
        style={CallLobbyComponentStyles.iconStyle}
        alt="camera-icon"
        src={
          remoteAuditState?.video
            ? "/icons/video_camera_front.svg"
            : "/icons/video_camera_front_off.svg"
        }
      />
    }
  />
);

// Helper Component for Toggle Camera Device Button
const ToggleCameraDeviceButton = ({
  videoTrack,
  remoteAuditState,
  handleToggleCameraDevice,
}) =>
  remoteAuditState?.isMultipleCamera &&
  videoTrack &&
  remoteAuditState?.video && (
    <Button
      style={CallLobbyComponentStyles.customButtons(
        remoteAuditState?.video ? variables.bodyBg : variables.primaryBg
      )}
      onClick={handleToggleCameraDevice}
      icon={
        <img
          style={CallLobbyComponentStyles.iconStyle}
          alt="toggle-camera-icon"
          src="/icons/camera_switch.svg"
        />
      }
    />
  );
